import { createFileRoute } from "@tanstack/react-router";
import Page from "@/pages/careers";

import { brand } from "@/config/brand";
const SITE_URL = brand.url;
const OG_IMAGE = `${SITE_URL}/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png`;
const OG_ALT = "Careers at Digitally Baffled — join our team";

// NOTE: Job posting dates must be kept current whenever roles are re-listed.
const ROLES_LISTED_DATE = "2026-07-05"; // Update this if the roles are re-listed in future.
const ROLES_CLOSE_DATE = "2027-01-05T23:59:59Z"; // Extend this if roles remain open past 6 months.

const commonJobFields = {
  "@context": "https://schema.org",
  employmentType: "CONTRACTOR",
  hiringOrganization: {
    "@type": "Organization",
    name: brand.legalName,
    sameAs: SITE_URL,
  },
  jobLocationType: "TELECOMMUTE",
  applicantLocationRequirements: { "@type": "Country", name: "United Kingdom" },
  directApply: true,
  url: `${SITE_URL}/careers`,
  datePosted: ROLES_LISTED_DATE,
  validThrough: ROLES_CLOSE_DATE,
} as const;

const jobJsonLd = [
  {
    ...commonJobFields,
    "@type": "JobPosting",
    title: "Sales Executive",
    description:
      "Commission-only role: earn 60% commission on every sale you close. Join Digitally Baffled as a Sales Executive and build your own client base using our brand, tools and billing. Flexible, fully remote and UK-based.",
  },
  {
    ...commonJobFields,
    "@type": "JobPosting",
    title: "Marketing Executive",
    description:
      "Commission-only role: earn 60% commission on results generated. Join Digitally Baffled as a Marketing Executive and grow your own portfolio using our brand, tools and billing. Flexible, fully remote and UK-based.",
  },
  {
    ...commonJobFields,
    "@type": "JobPosting",
    title: "Digital Content Producer",
    description:
      "Commission-only role: earn 60% commission on commissioned content projects. Join Digitally Baffled as a Digital Content Producer and deliver work under our brand, tools and billing. Flexible, fully remote and UK-based.",
  },
  {
    ...commonJobFields,
    "@type": "JobPosting",
    title: "Subcontractor Consultant",
    description:
      "Commission-only role: earn 60% commission on client work you deliver. Join Digitally Baffled as a Subcontractor Consultant and service clients using our brand, tools and billing. Flexible, fully remote and UK-based.",
  },
];

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: "Careers — Sales, Marketing, Content & Subcontractor Roles | Digitally Baffled" },
      {
        name: "description",
        content:
          "We're hiring a commission-based Sales Executive, Marketing Executive, Digital Content Producer and Subcontractor Consultants (60% commission). Build your own client base with our brand, tools and billing. Apply with your CV.",
      },
      { property: "og:site_name", content: "Digitally Baffled" },
      { property: "og:title", content: "Careers at Digitally Baffled — We're Hiring" },
      {
        property: "og:description",
        content:
          "Sales, Marketing, Content or Subcontractor Consultant — all commission-based, flexible and remote. Use our brand and portal to build your own client base. Apply with your CV.",
      },
      { property: "og:url", content: `${SITE_URL}/careers` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:secure_url", content: OG_IMAGE },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: OG_ALT },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Careers at Digitally Baffled — We're Hiring" },
      {
        name: "twitter:description",
        content:
          "Sales Executive, Marketing Executive, Digital Content Producer or Subcontractor Consultant — commission-based, flexible, remote. Build your own client base with our brand and tools.",
      },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: OG_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/careers` }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jobJsonLd) }],
  }),
  component: Page,
});
